import React, { useEffect, useId, useRef, useState } from "react";
import { Check, Loader2, Pencil, X } from "lucide-react";
import { getApiFieldErrors, getErrorMessage } from "@/services/api";
import LockedControl from "@/components/common/LockedControl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface InlineEditProps {
  value: string;
  /** What is edited, for screen readers, e.g. "company of a@b.com". */
  label: string;
  /** The API field whose server error is reported. */
  field: string;
  /** How a typed value is stored, e.g. trimmed and lowercased; trimmed by default. */
  normalize?: (value: string) => string;
  /** An error message for the normalized value, or null when it may be saved. */
  validate: (value: string) => string | null;
  onSave: (value: string) => Promise<unknown>;
  /** Shows a validation or server error where it can't be clipped (the page notice). */
  onError: (message: string) => void;
  /** Why the value can't be changed (the pencil is disabled and says why), or null. */
  lock?: string | null;
  type?: "text" | "email";
  /** The box both modes fill (its width), so opening the input never resizes the row. */
  className?: string;
  /** How the value reads when not editing; truncate it to fit the box. */
  children: React.ReactNode;
}

const trim = (value: string) => value.trim();

/**
 * A value with a pencil that turns it into an input: Enter or the check saves, Escape or the
 * cross cancels, and focus goes back to the pencil afterwards. Both modes fill the same
 * fixed box, and errors go to `onError` with the input marked invalid, so the table never
 * reflows.
 */
const InlineEdit: React.FC<InlineEditProps> = ({
  value,
  label,
  field,
  normalize = trim,
  validate,
  onSave,
  onError,
  lock = null,
  type = "text",
  className,
  children,
}) => {
  const errorId = useId();
  const pencilRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Select the value only on the focus that opens the editor, never on a refocus after an error.
  const selectOnFocus = useRef(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [returnFocus, setReturnFocus] = useState(false);

  useEffect(() => {
    if (!editing && returnFocus) {
      pencilRef.current?.focus();
      setReturnFocus(false);
    }
  }, [editing, returnFocus]);

  const start = () => {
    selectOnFocus.current = true;
    setDraft(value);
    setError(null);
    setEditing(true);
  };
  const stop = () => {
    setEditing(false);
    setReturnFocus(true);
  };
  const fail = (message: string) => {
    setError(message);
    onError(message);
  };
  const save = async () => {
    const next = normalize(draft);
    if (next === value) return stop();
    const invalid = validate(next);
    if (invalid) return fail(invalid);
    setSaving(true);
    try {
      await onSave(next);
      stop();
    } catch (err) {
      fail(getApiFieldErrors(err)[field] ?? getErrorMessage(err));
      inputRef.current?.focus();
    } finally {
      setSaving(false);
    }
  };

  const box = cn("flex h-8 min-w-0 items-center gap-1", className);

  if (!editing) {
    return (
      <span className={box}>
        {children}
        <LockedControl locked={!!lock} message={lock} inline className="shrink-0">
          <Button
            ref={pencilRef}
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
            onClick={start}
            aria-label={`Edit ${label}`}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        </LockedControl>
      </span>
    );
  }

  return (
    <form
      className={box}
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
      noValidate
    >
      <Input
        ref={inputRef}
        autoFocus
        // Text, not "email": selection ranges only work on text inputs; emails are validated here.
        type="text"
        inputMode={type === "email" ? "email" : undefined}
        onFocus={(event) => {
          if (!selectOnFocus.current) return;
          selectOnFocus.current = false;
          // Select it all with the start in view; a long value otherwise opens scrolled to its end.
          const input = event.currentTarget;
          input.setSelectionRange(0, input.value.length, "backward");
          input.scrollLeft = 0;
        }}
        value={draft}
        readOnly={saving}
        onChange={(event) => {
          setDraft(event.target.value);
          setError(null);
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape" && !saving) {
            event.preventDefault();
            stop();
          }
        }}
        className="h-8 min-w-0 flex-1 aria-[invalid=true]:border-destructive"
        aria-label={label}
        aria-invalid={!!error}
        aria-describedby={error ? errorId : undefined}
      />
      <Button
        type="submit"
        size="icon"
        className="h-8 w-8 shrink-0"
        disabled={saving}
        aria-label="Save"
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8 shrink-0"
        disabled={saving}
        onClick={stop}
        aria-label="Cancel"
      >
        <X className="h-4 w-4" />
      </Button>
      {error && (
        <span id={errorId} className="sr-only">
          {error}
        </span>
      )}
    </form>
  );
};

export default InlineEdit;
